import { create } from "zustand";

/**
 * Transient UI state (decision #13). This holds ONLY ephemeral state that must
 * not be persisted: current view, selected chat, open modals, vault lock state.
 * Persistent data (chats, messages, settings) is read reactively from IndexedDB
 * via useLiveQuery — never duplicated here. Streaming buffers bypass this store
 * entirely (decision #14) to avoid per-token re-renders on 6s+.
 */

export type AppView = "chats" | "characters" | "games" | "gallery" | "lorebooks" | "hub" | "guide" | "settings";

interface UiState {
  /** Which top-level section is shown. */
  view: AppView;
  setView: (view: AppView) => void;

  /** The chat currently open in the main pane (or null on the list). */
  activeChatId: string | null;
  setActiveChatId: (id: string | null) => void;

  /** Whether the responsive sidebar is open (mobile drawer). */
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebar: () => void;

  /** Whether the persistent sidebar (md+) is collapsed to give the main pane room. */
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebarCollapsed: () => void;

  /** Generic modal slot key, or null when nothing is open. */
  modal: string | null;
  openModal: (key: string) => void;
  closeModal: () => void;

  /** Key vault lock state mirror (decision #15); source of truth is the vault. */
  vaultUnlocked: boolean;
  setVaultUnlocked: (unlocked: boolean) => void;
}

export const useUiStore = create<UiState>((set) => ({
  view: "chats",
  setView: (view) => set({ view }),

  activeChatId: null,
  setActiveChatId: (activeChatId) => set({ activeChatId }),

  sidebarOpen: false,
  setSidebarOpen: (sidebarOpen) => set({ sidebarOpen }),
  toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),

  sidebarCollapsed: false,
  setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),
  toggleSidebarCollapsed: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),

  modal: null,
  openModal: (modal) => set({ modal }),
  closeModal: () => set({ modal: null }),

  vaultUnlocked: false,
  setVaultUnlocked: (vaultUnlocked) => set({ vaultUnlocked }),
}));
